<template>
  <Teleport to="body">
    <Transition name="sheet">
      <div
        v-if="show"
        class="fixed inset-0 z-50 flex flex-col justify-end"
        role="dialog"
        aria-modal="true"
        aria-label="Call Details"
      >
        <!-- Scrim -->
        <div class="absolute inset-0 bg-black/40" @click="$emit('close')" />

        <!-- Sheet panel -->
        <div class="relative max-h-[85vh] w-full overflow-y-auto rounded-t-2xl bg-white dark:bg-slate-900">
          <!-- Drag handle -->
          <div class="flex justify-center pt-3 pb-1">
            <div class="h-1 w-10 rounded-full bg-gray-300" />
          </div>

          <!-- Header -->
          <div class="flex items-center justify-between px-4 py-3">
            <h2 class="text-base font-semibold text-gray-900 dark:text-slate-100">Call Details</h2>
            <button
              class="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-slate-300"
              aria-label="Close"
              @click="$emit('close')"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Detail card -->
          <div class="mx-4 mb-4 overflow-hidden rounded-xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800">
            <div
              v-for="row in detailRows"
              :key="row.label"
              class="flex items-start justify-between border-b border-gray-100 dark:border-slate-800 px-4 py-3 last:border-0"
            >
              <span class="shrink-0 text-sm text-gray-500 dark:text-slate-400">{{ row.label }}</span>
              <span class="ml-4 text-right text-sm font-medium text-gray-900 dark:text-slate-100">{{ row.value }}</span>
            </div>
          </div>

          <!-- Call Legs -->
          <div v-if="calleesArray.length" class="mx-4 mb-6">
            <h3 class="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-slate-400">Call Legs</h3>
            <div
              v-for="(leg, idx) in calleesArray"
              :key="idx"
              class="mb-2 rounded-xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 px-4 py-3"
            >
              <p class="mb-2 text-sm font-medium text-gray-900 dark:text-slate-100">
                {{ leg.destinationNumber || leg.callerId || 'Unknown' }}
              </p>
              <div class="flex flex-wrap gap-1.5">
                <span
                  v-if="leg.talkTimeSec != null"
                  class="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-slate-300"
                >
                  {{ formatDuration(leg.talkTimeSec) }}
                </span>
                <span
                  v-if="leg.hangupCause"
                  class="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-slate-300"
                >
                  {{ leg.hangupCause }}
                </span>
              </div>
            </div>
          </div>

          <div v-else class="pb-6" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { CallRecord, CallCallee } from '~/types/calls';
import { isCallInbound, isCallAnswered, isCallMissed } from '~/utils/calls';
import { useContactStore } from '~/stores/contactStore';

const props = defineProps<{
  show: boolean;
  call: CallRecord | null;
}>();

defineEmits<{ close: [] }>();

const contactStore = useContactStore();

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function formatDuration(seconds: number | null | undefined): string {
  const s = seconds ?? 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function formatStartTime(epochSec: number): string {
  const d = new Date(epochSec * 1000);
  const day = DAY_NAMES[d.getDay()];
  const mon = MONTH_NAMES[d.getMonth()];
  const date = d.getDate();
  let h = d.getHours();
  const min = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${day}, ${mon} ${date} • ${h}:${min} ${ampm}`;
}

function getSummaryStatusLabel(summary: CallRecord['summary']): string {
  if (!summary) return 'Not started';
  const state = (summary.state ?? summary.s)?.toLowerCase();
  if (summary.text?.trim()) return 'Ready';
  if (state === 'f' || state === 'failed') return 'Failed';
  if (state === 'p-r') return 'Processing recording';
  if (state === 'g-s') return 'Generating summary';
  if (state) return 'In progress';
  return 'Not started';
}

// Normalise RTDB callees which may arrive as an array or a numeric-keyed object
const calleesArray = computed((): CallCallee[] => {
  const raw = props.call?.callees;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return Object.values(raw);
});

const detailRows = computed(() => {
  const call = props.call;
  if (!call) return [];

  const direction = isCallInbound(call) ? 'Inbound' : 'Outbound';
  const status = isCallAnswered(call) ? 'Answered' : isCallMissed(call) ? 'Missed' : 'Unknown';

  const rawFromNum = isCallInbound(call) ? (call.callerNumber || call.callerId || '') : (call.did || call.callerNumber || '');
  const fromName = contactStore.getNameForNumber(rawFromNum);
  const from = fromName ? `${fromName} (${rawFromNum})` : (rawFromNum || '—');

  const rawToNum = call.destinationNumber?.startsWith('555') ? null : (call.destinationNumber || null);
  const toName = rawToNum ? contactStore.getNameForNumber(rawToNum) : null;
  const to = toName && rawToNum ? `${toName} (${rawToNum})` : rawToNum;

  const rows = [
    { label: 'Direction', value: direction },
    { label: 'Status', value: status },
    { label: 'From', value: from },
    ...(to ? [{ label: 'To', value: to }] : []),
    ...(call.did ? [{ label: 'DID', value: call.did }] : []),
    { label: 'Started', value: formatStartTime(call.startEpoch) },
    { label: 'Duration', value: formatDuration(call.billSec ?? call.talkTimeSec) },
    ...(call.callType ? [{ label: 'Call type', value: call.callType.toUpperCase() }] : []),
    ...(call.hangupCause ? [{ label: 'Hangup cause', value: call.hangupCause }] : []),
    { label: 'Recording', value: (call.rec?.ts || call.recording?.objectName) ? 'Yes' : 'No' },
    { label: 'Summary', value: getSummaryStatusLabel(call.summary) },
    { label: 'Call ID', value: call.id },
  ];

  return rows;
});
</script>

<!-- Sheet slide-up / slide-down transition -->
<style scoped>
.sheet-enter-active,
.sheet-leave-active {
  transition: opacity 0.25s ease;
}
.sheet-enter-active .relative,
.sheet-leave-active .relative {
  transition: transform 0.3s ease;
}
.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;
}
.sheet-enter-from .relative {
  transform: translateY(100%);
}
.sheet-leave-to .relative {
  transform: translateY(100%);
}
</style>
